// src/services/axiosInstance.ts
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import {
  KEY_ACCESS_TOKEN,
  KEY_REFRESH_TOKEN,
  KEY_AZURE_TOKEN,
  KEY_LOGIN_ACCESS_TOKEN,
  REACT_CONFIG,
} from "../helpers/constant";

let appConfig: { apiBaseUrl: string; tokenUrl: string } | undefined;
let axiosInstance: AxiosInstance;
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

/**
 * Subscribe to token refresh to retry requests after refresh completes
 */
const subscribeTokenRefresh = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

const onRefreshed = (newToken: string) => {
  refreshSubscribers.map((cb) => cb(newToken));
  refreshSubscribers = [];
};

/**
 * Helper: Refresh access token
 */
const refreshAccessToken = async (): Promise<string | null> => {
  try {
    const refreshToken = localStorage.getItem(KEY_REFRESH_TOKEN);
    const azureToken = localStorage.getItem(KEY_AZURE_TOKEN);

    if (!refreshToken) {
      localStorage.clear();
      window.location.reload();
      return null;
    }

    const REFRESH_URL = `${appConfig?.apiBaseUrl}/refreshToken`;

    const res = await axios.post(
      REFRESH_URL,
      { refreshToken },
      {
        headers: {
          Authorization: `Bearer ${azureToken}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }
    );

    const { accessToken, refreshToken: newRefreshToken } = res.data;

    localStorage.setItem(KEY_ACCESS_TOKEN, accessToken.token);
    localStorage.setItem(KEY_REFRESH_TOKEN, newRefreshToken.token);

    return accessToken.token;
  } catch (err) {
    console.error("Token refresh failed:", err);
    localStorage.clear();
    window.location.reload();
    return null;
  }
};

/**
 * Create and configure Axios instance
 */
export const getAxiosInstance = async (): Promise<AxiosInstance> => {
  appConfig = REACT_CONFIG;

  if (axiosInstance) return axiosInstance;

  axiosInstance = axios.create({
    baseURL: appConfig?.apiBaseUrl || "https://ken-api-dev-2.ken42.com/sahyadri-node-dev/api/",
    timeout: 15000,
  });

  /**
   * Request Interceptor
   */
  axiosInstance.interceptors.request.use((req: any) => {
    const token = localStorage.getItem(KEY_ACCESS_TOKEN);
    const loginToken = localStorage.getItem(KEY_LOGIN_ACCESS_TOKEN);

    if (token) {
      req.headers = req.headers || {};
      req.headers["access-token"] = loginToken || "";
      req.headers.Authorization = `Bearer ${token}`;
    }
    return req;
  });

  /**
   * Response Interceptor
   */
  axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => {
      if (response?.data?.success === false) {
        console.error("Response Interceptor Error:", response.data.errorMessage);
      }
      return response;
    },
    async (error) => {
      const originalRequest = error.config;

      // Only handle 401 errors once per request
      if (error.response?.status !== 401) {
        return Promise.reject(error);
      }

      if (!isRefreshing) {
        isRefreshing = true;
        const newToken = await refreshAccessToken();
        isRefreshing = false;

        if (newToken) {
          onRefreshed(newToken);
          originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
          originalRequest.headers["access-token"] = newToken;
          return axiosInstance(originalRequest);
        }
      } else {
        // Queue failed requests until refresh completes
        return new Promise((resolve) => {
          subscribeTokenRefresh((newToken: string) => {
            originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
            originalRequest.headers["access-token"] = newToken;
            resolve(axiosInstance(originalRequest));
          });
        });
      }

      return Promise.reject(error);
    }
  );

  return axiosInstance;
};

/**
 * Example API call: Retrieve Bundle Exam Connection
 */


export const bundleExamConnection = async () => {
  const instance = await getAxiosInstance();
  const contactId = localStorage.getItem("contactId")
  const path = `/salesforce?endpoint=${encodeURIComponent(
    `/services/apexrest/examsportal/retrieve/evaluationRequest?contactId=${contactId}`
  )}`;

  try {
    const res = await instance.get(path);
    return res?.data ?? null;
  } catch (error) {
    console.error("bundleExamConnection error:", error);
    return null;
  }
};

export const evaluationData = async () => {
  const instance = await getAxiosInstance();
  const contactId = localStorage.getItem("contactId")
  // const contactId = "003Hz00000gNVYbIAO";
  const path = `/salesforce?endpoint=${encodeURIComponent(
    `/services/apexrest/examsportal/retrieve/evaluationRequest?contactId=${contactId}`
  )}`;

  try {
    const res = await instance.get(path);
    return res?.data ?? null;
  } catch (error) {
    console.error("bundleExamConnection error:", error);
    return null;
  }
};
export const invigilationData = async () => {
  const contactId = localStorage.getItem("contactId")
  const instance = await getAxiosInstance();
  // const contactId = "003Hz00000gNVYbIAO";
  const path = `/salesforce?endpoint=${encodeURIComponent(
    `/services/apexrest/examsportal/retrieve/invigilationRequest?contactId=${contactId}`
  )}`;

  try {
    const res = await instance.get(path);
    return res?.data ?? null;
  } catch (error) {
    console.error("bundleExamConnection error:", error);
    return null;
  }
};
export const allBooklets = async (payload: any) => {
  const contactId = localStorage.getItem("contactId")
  const instance = await getAxiosInstance();

  // const contactId = "003Hz00000gNVYbIAO";
  const path = `/salesforce?endpoint=${encodeURIComponent(
    `/services/apexrest/examsportal/retrieve/bundleExamConnection?bundleId=${payload}`
  )}`;

  try {
    const res = await instance.get(path);
    return res?.data ?? null;
  } catch (error) {
    console.error("bundleExamConnection error:", error);
    return null;
  }
};

export const getQuestionPaper = async (payload: any) => {
  const instance = await getAxiosInstance();
  const contactId = localStorage.getItem("contactId")
  const path = `/salesforce?endpoint=${encodeURIComponent(
    `/services/apexrest/examsportal/retrieve/examConnection?examConnectionId=${payload}&evaluationType=Retest`
  )}`;

  try {
    const res = await instance.get(path);
    return res?.data ?? null;
  } catch (error) {
    console.error("bundleExamConnection error:", error);
    return null;
  }
};

export const getMarksEvaluation = async (payload: any) => {
  const instance = await getAxiosInstance();
  const evaluationType = localStorage.getItem("evaluationType")

  const path = `/salesforce?endpoint=${encodeURIComponent(
    `/services/apexrest/examsportal/retrieve/examConnection?bundleId=${payload}&evaluationType=${evaluationType}`
  )}`;

  // const path = `/salesforce?endpoint=${encodeURIComponent(
  //   `/services/apexrest/examsportal/retrieve/examConnection?examConnectionId=${payload}&evaluationType=Retest`
  // )}`;

  try {
    const res = await instance.get(path);
    return res?.data ?? null;
  } catch (error) {
    console.error("bundleExamConnection error:", error);
    return null;
  }
};

export const updateBundleStatus = async (payload: { bundleId: string; bundleEvaluationStatus: string; fileUpload?: string }) => {
  const instance = await getAxiosInstance();
  const path = `/salesforce?endpoint=${encodeURIComponent(
    `/services/apexrest/examsportal/updatebundlestatus`
  )}`;

  try {
    const res = await instance.post(path, payload);
    return res?.data ?? null;
  } catch (error) {
    console.error("updateBundleStatus error:", error);
    return null;
  }
};


export const getReEvaluation = async () => {
  const instance = await getAxiosInstance();
  const contactId = localStorage.getItem("contactId")
  const path = `/salesforce?endpoint=${encodeURIComponent(
    `/services/apexrest/examsportal/retrieve/exams?contactId=${contactId}`
  )}`;

  try {
    const res = await instance.get(path);
    return res?.data ?? null;
  } catch (error) {
    console.error("bundleExamConnection error:", error);
    return null;
  }
};

export const getReExamination = async () => {
  const instance = await getAxiosInstance();
  const contactId = localStorage.getItem("contactId")
  const path = `/salesforce?endpoint=${encodeURIComponent(
    `/services/apexrest/examsportal/retrieve/exams?contactId=${contactId}`
  )}`;

  try {
    const res = await instance.get(path);
    return res?.data ?? null;
  } catch (error) {
    console.error("bundleExamConnection error:", error);
    return null;
  }
};

export const getUpComingExams = async () => {
  const instance = await getAxiosInstance();
  const contactId = localStorage.getItem("contactId")
  const path = `/salesforce?endpoint=${encodeURIComponent(
    `/services/apexrest/examsportal/retrieve/exams?contactId=${contactId}`
  )}`;

  try {
    const res = await instance.get(path);
    return res?.data ?? null;
  } catch (error) {
    console.error("bundleExamConnection error:", error);
    return null;
  }
};

export const getCompletedExams = async () => {
  const instance = await getAxiosInstance();
  const contactId = localStorage.getItem("contactId")
  const path = `/salesforce?endpoint=${encodeURIComponent(
    `/services/apexrest/examsportal/retrieve/exams?contactId=${contactId}`
  )}`;

  try {
    const res = await instance.get(path);
    return res?.data ?? null;
  } catch (error) {
    console.error("bundleExamConnection error:", error);
    return null;
  }
};

export const createStudentServiceRequest = async (payload: any) => {
  const instance = await getAxiosInstance();
  const path = `/salesforce?endpoint=${encodeURIComponent(
    `/services/apexrest/student/examrequests/createStudentServiceRequest`
  )}`;

  try {
    const res = await instance.post(path, payload);
    return res?.data ?? null;
  } catch (error) {
    console.error("createStudentServiceRequest error:", error);
    throw error;
  }
};

export const getMarksBreakdown = async () => {
  const instance = await getAxiosInstance();
  const contactId = localStorage.getItem("contactId");
  const path = `/salesforce?endpoint=${encodeURIComponent(
    `/services/apexrest/examsportal/retrieve/semesterResults?contactId=${contactId}`
  )}`;

  try {
    const res = await instance.get(path);
    return res?.data ?? null;
  } catch (error) {
    console.error("bundleExamConnection error:", error);
    return null;
  }
};

export const downloadResultPdf = async (
  attachmentID: string
): Promise<Blob | null> => {
  const instance = await getAxiosInstance();

  if (!attachmentID) {
    console.error("downloadResultPdf: contactId is required");
    return null;
  }

  const path = `/salesforce?endpoint=${encodeURIComponent(
    `/services/apexrest/examsportal/retrieve/downloadResultPdf?contentDocumentId=${attachmentID}`
  )}`;

  try {
    const res = await instance.get(path, { responseType: "blob" });
    return res?.data ?? null;
  } catch (error) {
    console.error("downloadResultPdf error:", error);
    return null;
  }
};
export const getMarksBreakdownResult = async (courseId: any) => {
  const instance = await getAxiosInstance();
  if (!courseId) {
    console.error("getMarksBreakdownResult: courseConnectionId is required");
    return null;
  }
  const path = `/salesforce?endpoint=${encodeURIComponent(
    `/services/apexrest/examsportal/retrieve/courseConnectionScores?courseConnectionId=${courseId}`
  )}`;

  try {
    const res = await instance.get(path);
    return res?.data ?? null;
  } catch (error) {
    console.error("bundleExamConnection error:", error);
    return null;
  }
};

export const postLoginEmail = async (email: string) => {
  const instance = await getAxiosInstance();
  const contactId = localStorage.getItem("contactId")
  const path = `/salesforce?endpoint=${encodeURIComponent(
    `/services/apexrest/otp/send?contact=${email}`
  )}`;

  try {
    const res = await instance.post(path, {});
    return res?.data ?? null;
  } catch (error) {
    console.error("bundleExamConnection error:", error);
    return null;
  }
}

export const verifyOtp = async (email: string, otp: string) => {
  const instance = await getAxiosInstance();
  const contactId = localStorage.getItem("contactId")
  const path = `/salesforce?endpoint=${encodeURIComponent(
    `/services/apexrest/otp/verify?contact=${email}&otpentered=${otp}`
  )}`;

  try {
    const res = await instance.get(path);
    return res?.data ?? null;
  } catch (error) {
    console.error("bundleExamConnection error:", error);
    return null;
  }
}

export const postUpdateEvaluation = async (payload: any) => {
  const instance = await getAxiosInstance();
  const contactId = localStorage.getItem("contactId")
  const path = `/salesforce?endpoint=${encodeURIComponent(
    `/services/apexrest/examsportal/updateevaluation`
  )}`;

  try {
    const res = await instance.post(path, payload);
    return res?.data ?? null;
  } catch (error) {
    console.error("bundleExamConnection error:", error);
    return null;
  }
}
